import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '../ui/sheet';
import { api } from '../../lib/api';

const baseNav = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About Us' },
  // Oman Trips is rendered as its own dropdown below
  { to: '/shop', label: 'Shop' },
  { to: '/team', label: 'Our Team' },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [coursesOpen, setCoursesOpen] = useState(false);
  const [divingOpen, setDivingOpen] = useState(false);
  const [tripsOpen, setTripsOpen] = useState(false);
  const [trips, setTrips] = useState([]);
  const coursesTimerRef = useRef(null);
  const divingTimerRef = useRef(null);
  const tripsTimerRef = useRef(null);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Load trips for both dropdowns — refresh on route change so admin edits surface quickly
  useEffect(() => {
    api.get('/trips').then(({ data }) => setTrips(Array.isArray(data) ? data : [])).catch(() => setTrips([]));
  }, [loc.pathname]);

  // Close menus on route change
  useEffect(() => { setCoursesOpen(false); setDivingOpen(false); setTripsOpen(false); }, [loc.pathname]);

  // Clean up pending timers on unmount
  useEffect(() => () => {
    [coursesTimerRef, divingTimerRef, tripsTimerRef].forEach((r) => { if (r.current) clearTimeout(r.current); });
  }, []);

  const makeHover = (setter, timerRef) => ({
    open: () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      setter(true);
    },
    close: () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setter(false), 220);
    },
  });
  const coursesHover = makeHover(setCoursesOpen, coursesTimerRef);
  const divingHover = makeHover(setDivingOpen, divingTimerRef);
  const tripsHover = makeHover(setTripsOpen, tripsTimerRef);

  const divingTrips = trips.filter((t) => (t.category || 'oman_tours') === 'diving_trips');
  const omanTours = trips.filter((t) => (t.category || 'oman_tours') === 'oman_tours');

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? 'bg-[#07101f]/95 border-white/10 backdrop-blur supports-[backdrop-filter]:bg-[#07101f]/80' : 'bg-[#0a1628]/60 border-transparent backdrop-blur'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
          <img src="/brand/logo.jpeg" alt="Arab Diver Tours logo" className="h-10 w-10 rounded-full object-cover ring-1 ring-[#c8e600]/40" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-sm font-semibold tracking-tight text-white">Arab Diver Tours</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-white/55">Scuba Diving</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 text-sm">
          {baseNav.slice(0, 2).map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `link-underline relative pb-1 font-medium ${isActive ? 'text-white' : 'text-white/70 hover:text-white'}`}>
              {n.label}
            </NavLink>
          ))}

          {/* Courses dropdown */}
          <div className="relative" onMouseEnter={coursesHover.open} onMouseLeave={coursesHover.close}>
            <button
              type="button"
              data-testid="navbar-courses-trigger"
              onClick={() => setCoursesOpen((v) => !v)}
              onFocus={coursesHover.open}
              className={`link-underline inline-flex items-center gap-1 pb-1 font-medium ${loc.pathname.startsWith('/courses') ? 'text-white' : 'text-white/70 hover:text-white'}`}
              aria-expanded={coursesOpen}
              aria-haspopup="menu"
            >
              Courses <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {coursesOpen && (
              <div
                data-testid="navbar-courses-menu"
                onMouseEnter={coursesHover.open}
                onMouseLeave={coursesHover.close}
                className="absolute left-0 top-full pt-3 w-64"
              >
                <div className="rounded-2xl border border-white/12 bg-[#07101f]/98 backdrop-blur p-2 shadow-2xl">
                  <Link to="/courses/sdi" data-testid="navbar-link-sdi" className="block rounded-xl px-3 py-2 hover:bg-white/8">
                    <div className="font-display font-semibold text-white">SDI — Recreational</div>
                    <div className="text-xs text-white/55">Open Water through Instructor Trainer</div>
                  </Link>
                  <Link to="/courses/tdi" data-testid="navbar-link-tdi" className="block rounded-xl px-3 py-2 hover:bg-white/8">
                    <div className="font-display font-semibold text-white">TDI — Technical</div>
                    <div className="text-xs text-white/55">Open Circuit, Rebreather, Cave & more</div>
                  </Link>
                  <Link to="/courses/frti" data-testid="navbar-link-frti" className="block rounded-xl px-3 py-2 hover:bg-white/8">
                    <div className="font-display font-semibold text-white">FRTI — First Response Training</div>
                    <div className="text-xs text-white/55">Emergency Care · Oxygen Administration</div>
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Diving Trips dropdown — auto-populated from /api/trips with category=diving_trips */}
          <div className="relative" onMouseEnter={divingHover.open} onMouseLeave={divingHover.close}>
            <button
              type="button"
              data-testid="navbar-diving-trigger"
              onClick={() => setDivingOpen((v) => !v)}
              onFocus={divingHover.open}
              className={`link-underline inline-flex items-center gap-1 pb-1 font-medium text-white/70 hover:text-white`}
              aria-expanded={divingOpen}
              aria-haspopup="menu"
            >
              Diving Trips <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {divingOpen && (
              <div
                data-testid="navbar-diving-menu"
                onMouseEnter={divingHover.open}
                onMouseLeave={divingHover.close}
                className="absolute left-0 top-full pt-3 w-72"
              >
                <div className="rounded-2xl border border-white/12 bg-[#07101f]/98 backdrop-blur p-2 shadow-2xl">
                  {divingTrips.length === 0 && (
                    <div className="px-3 py-2 text-xs text-white/55">No diving trips yet</div>
                  )}
                  {divingTrips.map((t) => (
                    <Link
                      key={t.id || t.slug}
                      to={`/trips/${t.slug}`}
                      data-testid={`navbar-diving-link-${t.slug}`}
                      className="block rounded-xl px-3 py-2 hover:bg-white/8"
                    >
                      <div className="font-display font-semibold text-white">{t.name}</div>
                      {t.short_description && (
                        <div className="text-xs text-white/55 line-clamp-1">{t.short_description}</div>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Oman Tours dropdown — auto-populated from /api/trips with category=oman_tours */}
          <div className="relative" onMouseEnter={tripsHover.open} onMouseLeave={tripsHover.close}>
            <button
              type="button"
              data-testid="navbar-trips-trigger"
              onClick={() => setTripsOpen((v) => !v)}
              onFocus={tripsHover.open}
              className={`link-underline inline-flex items-center gap-1 pb-1 font-medium ${loc.pathname.startsWith('/trips') ? 'text-white' : 'text-white/70 hover:text-white'}`}
              aria-expanded={tripsOpen}
              aria-haspopup="menu"
            >
              Oman Tours <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {tripsOpen && (
              <div
                data-testid="navbar-trips-menu"
                onMouseEnter={tripsHover.open}
                onMouseLeave={tripsHover.close}
                className="absolute left-0 top-full pt-3 w-72"
              >
                <div className="rounded-2xl border border-white/12 bg-[#07101f]/98 backdrop-blur p-2 shadow-2xl">
                  {omanTours.length === 0 && (
                    <div className="px-3 py-2 text-xs text-white/55">No tours yet</div>
                  )}
                  {omanTours.map((t) => (
                    <Link
                      key={t.id || t.slug}
                      to={`/trips/${t.slug}`}
                      data-testid={`navbar-trip-link-${t.slug}`}
                      className="block rounded-xl px-3 py-2 hover:bg-white/8"
                    >
                      <div className="font-display font-semibold text-white">{t.name}</div>
                      {t.short_description && (
                        <div className="text-xs text-white/55 line-clamp-1">{t.short_description}</div>
                      )}
                    </Link>
                  ))}
                  <div className="mt-1 border-t border-white/8 pt-1">
                    <Link to="/trips" className="block rounded-xl px-3 py-2 text-xs text-[#00b4d8] hover:bg-white/8">
                      View all tours →
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {baseNav.slice(2).map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `link-underline relative pb-1 font-medium ${isActive ? 'text-white' : 'text-white/70 hover:text-white'}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/courses/sdi">
            <Button data-testid="navbar-cta-button" className="bg-[#c8e600] text-[#0a1628] hover:bg-[#b6d100] font-semibold rounded-xl">
              Start Learning
            </Button>
          </Link>
        </div>

        {/* Mobile menu */}
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden text-white" data-testid="navbar-mobile-toggle" aria-label="Open menu">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-[#07101f] border-white/10 text-white w-[300px]">
            <div className="flex items-center gap-3 mb-6 mt-2">
              <img src="/brand/logo.jpeg" alt="" className="h-10 w-10 rounded-full object-cover ring-1 ring-[#c8e600]/40" />
              <div className="flex flex-col">
                <span className="font-display font-semibold">Arab Diver Tours</span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-white/55">Scuba Diving</span>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              {baseNav.map((n) => (
                <SheetClose asChild key={n.to}>
                  <NavLink to={n.to} className={({ isActive }) => `px-3 py-3 rounded-xl ${isActive ? 'bg-white/8 text-white' : 'text-white/80 hover:bg-white/6'}`}>
                    {n.label}
                  </NavLink>
                </SheetClose>
              ))}

              <div className="mt-3 pt-3 border-t border-white/10">
                <div className="text-xs uppercase tracking-wide text-white/50 px-3 mb-1">Diving Trips</div>
                {divingTrips.map((t) => (
                  <SheetClose asChild key={t.id || t.slug}>
                    <NavLink to={`/trips/${t.slug}`} className="block px-3 py-2 rounded-xl text-white/80 hover:bg-white/6">{t.name}</NavLink>
                  </SheetClose>
                ))}
                {divingTrips.length === 0 && (
                  <div className="px-3 py-2 text-xs text-white/45">No diving trips yet</div>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-white/10">
                <div className="text-xs uppercase tracking-wide text-white/50 px-3 mb-1">Oman Tours</div>
                {omanTours.map((t) => (
                  <SheetClose asChild key={t.id || t.slug}>
                    <NavLink to={`/trips/${t.slug}`} className="block px-3 py-2 rounded-xl text-white/80 hover:bg-white/6">{t.name}</NavLink>
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  <NavLink to="/trips" className="block px-3 py-2 rounded-xl text-xs text-[#00b4d8] hover:bg-white/6">View all tours →</NavLink>
                </SheetClose>
              </div>

              <div className="mt-3 pt-3 border-t border-white/10">
                <div className="text-xs uppercase tracking-wide text-white/50 px-3 mb-1">Courses</div>
                <SheetClose asChild>
                  <NavLink to="/courses/sdi" className="block px-3 py-2 rounded-xl text-white/80 hover:bg-white/6">SDI — Recreational</NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/courses/tdi" className="block px-3 py-2 rounded-xl text-white/80 hover:bg-white/6">TDI — Technical</NavLink>
                </SheetClose>
                <SheetClose asChild>
                  <NavLink to="/courses/frti" className="block px-3 py-2 rounded-xl text-white/80 hover:bg-white/6">FRTI — First Response Training</NavLink>
                </SheetClose>
              </div>

              <div className="mt-4">
                <SheetClose asChild>
                  <Link to="/courses/sdi">
                    <Button className="w-full bg-[#c8e600] text-[#0a1628] hover:bg-[#b6d100] font-semibold rounded-xl">Start Learning</Button>
                  </Link>
                </SheetClose>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
